import React, { useState, useEffect, Fragment } from 'react';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import { Box, ListItemButton, IconButton, Button, Tooltip } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import store from '../../../store/index';

import DashboardIcon from '../../icons/DashboardIcon';
import UsersIcon from '../../icons/UsersIcon';
import StoresIcon from '../../icons/StoresIcon';
import SettingsIcon from '../../icons/SettingsIcon';
import BellIcon from '../../icons/BellIcon';
import LogoIcon from '../../icons/LogoIcon';
import LogoutIcon from '../../icons/LogoutIcon';
import BackIcon from '../../icons/BackArrowIcon';
import InventoryScanIcon from '../../icons/InventoryScanIcon';

import LanguageSelect from '../../partials/LanguageSelect';
import { ThemeToggle } from '../../partials/CustomToggle';

import DrawerHeader from '../../styled/DrawerHeader';
import { FlexCenter, FlexColumn } from '../../styled/FlexBox';
import apiClient from '../../../services/api';

const drawerWidth = window.innerWidth > 650 ? 88 : 56;
const drawerWidthOpen = 240;

const openedMixin = (theme) => ({
    width: drawerWidthOpen,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
});

const closedMixin = (theme) => ({
    width: drawerWidth,
    overflowX: 'hidden',
    [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
    },
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
});

const getModuleStatus = (modules, moduleName) => {
    let isEnabled = false;

    if(!modules || modules.length === 0) {
        return false;
    }
    
    modules.forEach(function (module) {
        if(module.name === moduleName) {
            isEnabled = module.is_enabled;
        }
    })

    return isEnabled;
}

const SidebarDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '&, & .MuiDrawer-paper':
            open ? openedMixin(theme) : closedMixin(theme),
        '& .MuiDrawer-paper': {
            height: 'unset',
            minHeight: '100%',
            padding: theme.spacing(window.innerWidth > 605 ? 2 : 0),
        }
    }),
);

const SidebarListItem = styled(ListItem)(
    ({ theme }) => ({
        display: 'block',
        width: 48,
        height: 48,
        '& + &': {
            marginTop: theme.spacing(window.innerWidth < 650 ? 1 : 4)
        }
    }),
);

const SidebarListItemButton = styled(ListItemButton)(
    ({ theme }) => ({
        padding: 0,
        minHeight: 48,
        justifyContent: 'center',
        borderRadius: 12,
        "&:hover": {
            backgroundColor: theme.palette.background.navbarButton
        },
        '&.Mui-selected, &:hover.Mui-selected': {
            backgroundColor: theme.palette.background.button,
        },
    }),
);

const SidebarButton = styled(Button)(
    ({ theme }) => ({
        display: 'flex',
        width: 48,
        height: 48,
        minWidth: 'unset',
        justifyContent: 'center',
        borderRadius: '12px',
        backgroundColor: theme.palette.background.navbarButton,
    }),
);

export default function DashboardLeftSidebar({ open = false, setIsSidebarOpened, isMobile }) {
    //const modules = useSelector((state) => state.auth.modules);
    
    const [isEasyFloorEnabled, setIsEasyFloorEnabled] = useState(false);
    const [isEasyBoardOnlineEnabled, setIsEasyBoardOnlineEnabled] = useState(false);
    const [isUsersEnabled, setIsUsersEnabled] = useState(false);
    const [isInventoryScanEnabled, setIsInventoryScanEnabled] = useState(false);
    const [isSettingsEnabled, setIsSettingsEnabled] = useState(false);
    
    const { t } = useTranslation();

    useEffect(() => {
        apiClient.post('/api/modules/list').then(response => {
            const modules = response.data;

            setIsEasyFloorEnabled(getModuleStatus(modules, 'easyFloor'))
            setIsEasyBoardOnlineEnabled(getModuleStatus(modules, 'easyBoardOnline'))
            setIsUsersEnabled(getModuleStatus(modules, 'users'))
            setIsInventoryScanEnabled(getModuleStatus(modules, 'scannedProjects'))
            setIsSettingsEnabled(getModuleStatus(modules, 'settings'))
        }).catch(error => {})
    }, []);

    const items = [
        {
            href: "/app/easy-floor",
            icon: StoresIcon,
            title: t("Floor Plans"),
            enabled: isEasyFloorEnabled
        },
        {
            href: "/app/project",
            icon: DashboardIcon,
            title: t('Planograms'),
            enabled: isEasyBoardOnlineEnabled
        },
        {
            href: "/app/scanned-project",
            icon: InventoryScanIcon,
            title: t('Automatic Planograms'),
            enabled: isInventoryScanEnabled
        },
        {
            href: "/app/users",
            icon: UsersIcon,
            title: t("User Management"),
            enabled: isUsersEnabled
        },
        {
            href: "/app/settings",
            icon: SettingsIcon,
            title: t("Settings"),
            id: 'settings',
            enabled: isSettingsEnabled
        },
        /*
        {
            href: "/app/customers",
            icon: CustomersIcon,
            title: t("Customer Management"),
            enabled: checkIfModuleEnabled(modules, 'customers')
        },
        //*/
    ];

    const handleLogout = (e) => {
        e.preventDefault();
        store.dispatch({ type: "SET_LOGOUT" });
    };

    const location = useLocation();

    const pathMatch = (path) => !!matchPath({ path, end: false }, location.pathname);

    function handleCloseSidebar() {
        setIsSidebarOpened(false);
    }

    return (
        <Fragment>
            <Box>
                <SidebarDrawer variant="permanent" open={open}>
                    <DrawerHeader display='block'>
                        {
                            isMobile
                                ?   <Button
                                        onClick={handleCloseSidebar}
                                        variant='outlined'
                                        size='small'
                                        style={{
                                            maxWidth: '56px',
                                            minWidth: '56px',
                                            marginBottom: '5px'
                                        }}
                                    >
                                        <BackIcon />
                                    </Button>
                                : ''
                        }
                        <Tooltip title={t('Dashboard')} placement="right">
                            <RouterLink to="/app/home" style={{display: 'block'}}>
                                <LogoIcon />
                            </RouterLink>
                        </Tooltip>
                    </DrawerHeader>
                    <FlexColumn flexGrow={1}>
                        <FlexCenter paddingTop={window.innerWidth > 650 ? 4 : 1}>
                            <List disablePadding>
                                {items.map((item, index) => (
                                    <SidebarListItem key={index} disablePadding>
                                        <SidebarListItemButton
                                            disabled={!item.enabled}
                                            component={RouterLink}
                                            to={item.href}
                                            selected={pathMatch(item.href)}
                                        >
                                            <Tooltip title={item.title} placement='right'>
                                                <item.icon isActive={pathMatch(item.href)} />
                                                <ListItemText hidden={!open} primary={item.title} />
                                            </Tooltip>
                                        </SidebarListItemButton>
                                    </SidebarListItem>
                                ))}
                            </List>
                        </FlexCenter>
                        <FlexCenter paddingTop={window.innerWidth > 650 ? 4 : 1}>
                            <SidebarButton>
                                <BellIcon dot={true} />
                            </SidebarButton>
                        </FlexCenter>
                    </FlexColumn>
                    <FlexColumn paddingTop={window.innerWidth > 650 ? 4 : 1} paddingBottom={1}>
                        <FlexCenter paddingBottom={window.innerWidth > 650 ? 4 : 1}>
                                <Tooltip title={t('Logout')} placement='right'>
                                    <IconButton size="large" onClick={handleLogout}>
                                        <LogoutIcon />
                                    </IconButton>
                                </Tooltip>
                        </FlexCenter>
                        <FlexCenter paddingBottom={window.innerWidth > 650 ? 4 : 2}>
                            <ThemeToggle />
                        </FlexCenter>
                        <FlexCenter>
                            <LanguageSelect />
                        </FlexCenter>
                    </FlexColumn>
                </SidebarDrawer>
            </Box>
        </Fragment>
    );
}
